import React, { useCallback, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import toast from 'react-hot-toast';

import {
  RiskReviewService,
  RegistrationRiskLog,
  EnforcementMode,
  ReviewOutcome,
} from 'us-web-services';
import { colors } from 'us-react-components/dist/constants/Styles';
import PageStyles from 'src/styles/PageStyles';

import { ENFORCEMENT_OPTIONS } from './constants';
import ReviewRow from './components/ReviewRow';

function RiskReview() {
  const { classes } = PageStyles();
  const [items, setItems] = useState<RegistrationRiskLog[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [tierFilter, setTierFilter] = useState<string>('');
  const [reviewedFilter, setReviewedFilter] = useState<string>('false');
  const [loading, setLoading] = useState(false);
  const [enforcementMode, setEnforcementMode] = useState<EnforcementMode>('OFF');
  const [enforcementLoading, setEnforcementLoading] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState<EnforcementMode | null>(null);
  const [scanDialogOpen, setScanDialogOpen] = useState(false);
  const [scanStartDate, setScanStartDate] = useState('');
  const [scanEndDate, setScanEndDate] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await RiskReviewService.list({
        riskTier: tierFilter || undefined,
        reviewed: reviewedFilter !== '' ? reviewedFilter === 'true' : undefined,
        limit: rowsPerPage,
        offset: page * rowsPerPage,
      });

      setItems(response.data.data);
      setTotal(response.data.meta.size);
    } catch (error) {
      console.warn('Risk review fetch failed:', error);
      toast.error('Failed to load risk review data');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, tierFilter, reviewedFilter]);

  const fetchEnforcementMode = useCallback(async () => {
    try {
      const response = await RiskReviewService.getEnforcementMode();

      setEnforcementMode(response.data.data.mode);
    } catch (error) {
      console.warn('Enforcement mode fetch failed:', error);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    fetchEnforcementMode();
  }, [fetchEnforcementMode]);

  const handleEnforcementConfirm = async () => {
    const newMode = confirmDialog;

    setConfirmDialog(null);
    if (!newMode) return;

    setEnforcementLoading(true);
    try {
      const response = await RiskReviewService.setEnforcementMode(newMode);

      setEnforcementMode(response.data.data.mode);

      const label = ENFORCEMENT_OPTIONS.find((o) => o.value === newMode)?.label ?? newMode;

      toast.success(`Enforcement mode set to: ${label}`);
    } catch (error) {
      console.warn('Enforcement mode update failed:', error);
      toast.error('Failed to update enforcement mode');
    } finally {
      setEnforcementLoading(false);
    }
  };

  const handleResolve = async (id: number, outcome: ReviewOutcome) => {
    try {
      await RiskReviewService.resolveReview(id, outcome);
      toast.success(outcome === 'UNBLOCKED' ? 'User unblocked' : 'Block confirmed');
      fetchData();
    } catch (error) {
      console.warn('Risk review resolve failed:', error);
      toast.error('Failed to resolve review');
    }
  };

  const handleTriggerScan = async () => {
    setScanDialogOpen(false);
    try {
      toast('Retroactive scan started...');

      const params: { startDate?: string; endDate?: string } = {};

      if (scanStartDate) params.startDate = scanStartDate;
      if (scanEndDate) params.endDate = scanEndDate;

      const response = await RiskReviewService.triggerScan(
        Object.keys(params).length > 0 ? params : undefined
      );

      const result = response.data.data;

      toast.success(
        `Scan complete: ${result.scanned} scanned, ${result.flagged} flagged`
      );
      fetchData();
    } catch (error) {
      console.warn('Retroactive scan failed:', error);
      toast.error('Failed to trigger scan');
    }
  };

  const handleBackfill = async () => {
    try {
      toast('Backfill started...');
      const response = await RiskReviewService.backfillSignals();
      const result = response.data.data;

      toast.success(
        `Backfill complete: ${result.processed} processed, ${result.failed} failed`
      );
    } catch (error) {
      console.warn('Backfill failed:', error);
      toast.error('Failed to run backfill');
    }
  };

  const pendingOption = ENFORCEMENT_OPTIONS.find((o) => o.value === confirmDialog);

  return (
    <Paper className={classes.paper}>
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant='h5'>Risk Review</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant='outlined' size='small' onClick={handleBackfill}>
              Backfill Signals
            </Button>
            <Button variant='outlined' size='small' onClick={() => setScanDialogOpen(true)}>
              Retroactive Scan
            </Button>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <FormControl size='small' sx={{ minWidth: 200 }}>
            <InputLabel>Enforcement Mode</InputLabel>
            <Select
              value={enforcementMode}
              label='Enforcement Mode'
              disabled={enforcementLoading}
              onChange={(e) => {
                const nextMode = e.target.value as EnforcementMode;

                if (nextMode !== enforcementMode) {
                  setConfirmDialog(nextMode);
                }
              }}
            >
              {ENFORCEMENT_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant='body2' sx={{ color: colors.gray[500] }}>
            {ENFORCEMENT_OPTIONS.find((o) => o.value === enforcementMode)?.description}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
          <FormControl size='small' sx={{ minWidth: 150 }}>
            <InputLabel>Risk Tier</InputLabel>
            <Select
              value={tierFilter}
              label='Risk Tier'
              onChange={(e) => {
                setTierFilter(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value=''>All</MenuItem>
              <MenuItem value='CONFIRMED'>Confirmed</MenuItem>
              <MenuItem value='MEDIUM_HIGH'>Medium-High</MenuItem>
              <MenuItem value='LOW'>Low</MenuItem>
            </Select>
          </FormControl>

          <FormControl size='small' sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={reviewedFilter}
              label='Status'
              onChange={(e) => {
                setReviewedFilter(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value=''>All</MenuItem>
              <MenuItem value='false'>Pending Review</MenuItem>
              <MenuItem value='true'>Resolved</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <TableContainer>
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 80 }}>Risk</TableCell>
                <TableCell sx={{ width: 80 }}>User</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Matched Signals</TableCell>
                <TableCell sx={{ width: 200, whiteSpace: 'nowrap' }}>Date</TableCell>
                <TableCell sx={{ width: 220 }}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align='center'>
                    Loading...
                  </TableCell>
                </TableRow>
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align='center'>
                    No results found
                  </TableCell>
                </TableRow>
              ) : (
                items.map((item) => (
                  <ReviewRow key={item.id} item={item} onResolve={handleResolve} />
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component='div'
          count={total}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50]}
        />
      </Box>

      <Dialog open={!!confirmDialog} onClose={() => setConfirmDialog(null)}>
        <DialogTitle>Change Enforcement Mode</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to change the enforcement mode
            to <strong>{pendingOption?.label}</strong>?
          </DialogContentText>
          <DialogContentText sx={{ mt: 1 }}>
            {pendingOption?.description}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog(null)} disabled={enforcementLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleEnforcementConfirm}
            variant='contained'
            disabled={enforcementLoading}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={scanDialogOpen} onClose={() => setScanDialogOpen(false)}>
        <DialogTitle>Retroactive Scan</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Scan active users for matches against blocked user signals.
            Optionally filter by account creation date.
          </DialogContentText>
          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <TextField
              label='Start Date'
              type='date'
              size='small'
              value={scanStartDate}
              onChange={(e) => setScanStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label='End Date'
              type='date'
              size='small'
              value={scanEndDate}
              onChange={(e) => setScanEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScanDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleTriggerScan} variant='contained'>
            Start Scan
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}

export default RiskReview;
